/**
 * platform-user.ts
 * Shared Zod contracts for platform-tier user management (KI-36 / AZ-11 / UM-6).
 *
 * Covers:
 *   - CreatePlatformUserBody     : POST /platform/companies/:id/users (KI-33)
 *   - CreatePlatformUserResponse : 201 response
 *   - PatchPlatformUserBody      : PATCH /platform/companies/:id/users/:userId (KI-34)
 *   - AssignPlatformRoleBody     : POST .../users/:userId/roles (KI-35)
 *   - PlatformUserSchema         : user DTO returned by list/detail
 *   - PlatformUserListResponse   : array wrapper
 *   - SetUserCapabilitiesBody    : PUT .../users/:userId/capabilities (AZ-10.2 / AZ-11)
 *   - UserCapabilityResponse     : GET .../users/:userId/capabilities (AZ-10.1 / AZ-11)
 *   - UserAccessResponse         : GET .../users/:userId/access (AZ-10.1 / UM-7)
 *
 * ZERO Hono / Drizzle / server-runtime imports — Zod only (KI-36.1 / AZ-11.2).
 *
 * Spec: KI-33..KI-36 / AZ-9..AZ-11 / UM-3..UM-8
 */
import { z } from 'zod';

// ---------------------------------------------------------------------------
// KI-33: Platform User Creation
// ---------------------------------------------------------------------------

/**
 * POST /platform/companies/:id/users request body.
 * NO companyId field — company comes exclusively from the :id path param (KI-33.4).
 * Scenario KI-36-B: companyId must be absent from the parsed output.
 */
export const CreatePlatformUserBodySchema = z.object({
  email: z.string().email('Valid email required'),
  /** Required for password companies; absent for Microsoft/Google ones (company.sign_in_method). */
  password: z.string().min(8, 'Password must be at least 8 characters').optional(),
  firstName: z.string().optional(),
  lastName: z.string().optional(),
  sector: z.string().optional(),
  phone: z.string().optional(),
  /** Optional: assign an initial role at creation time (KI-33.1). */
  roleId: z.string().uuid().optional(),
});

export type CreatePlatformUserBody = z.infer<typeof CreatePlatformUserBodySchema>;

/**
 * 201 response for user creation — minimum: id + email (KI-33.6).
 */
export const CreatePlatformUserResponseSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
});

export type CreatePlatformUserResponse = z.infer<typeof CreatePlatformUserResponseSchema>;

// ---------------------------------------------------------------------------
// KI-34: Platform User Edit
// ---------------------------------------------------------------------------

/**
 * PATCH /platform/companies/:id/users/:userId — active flag toggle.
 */
export const PatchPlatformUserActiveBodySchema = z.object({
  active: z.boolean(),
});

export type PatchPlatformUserActiveBody = z.infer<typeof PatchPlatformUserActiveBodySchema>;

/**
 * PATCH /platform/companies/:id/users/:userId — profile fields.
 * At least one field must be present (KI-34.1).
 * Scenario KI-36-C: empty object must throw ZodError.
 */
export const PatchPlatformUserProfileBodySchema = z
  .object({
    active: z.boolean().optional(),
    firstName: z.string().optional(),
    lastName: z.string().optional(),
    sector: z.string().optional(),
    phone: z.string().optional(),
  })
  .refine(
    (data) =>
      data.active !== undefined ||
      data.firstName !== undefined ||
      data.lastName !== undefined ||
      data.sector !== undefined ||
      data.phone !== undefined,
    { message: 'At least one field must be provided.' },
  );

export type PatchPlatformUserProfileBody = z.infer<typeof PatchPlatformUserProfileBodySchema>;

/**
 * Combined PATCH body — server picks the right sub-schema.
 * Exported for consumers that want the union type.
 */
export const PatchPlatformUserBodySchema = PatchPlatformUserProfileBodySchema;
export type PatchPlatformUserBody = PatchPlatformUserProfileBody;

// ---------------------------------------------------------------------------
// KI-35: Role Assignment / Revocation
// ---------------------------------------------------------------------------

/**
 * POST /platform/companies/:id/users/:userId/roles request body (KI-35.1).
 */
export const AssignPlatformRoleBodySchema = z.object({
  roleId: z.string().uuid('roleId must be a valid UUID'),
});

export type AssignPlatformRoleBody = z.infer<typeof AssignPlatformRoleBodySchema>;

// ---------------------------------------------------------------------------
// KI-36: User DTO (returned by list and detail endpoints)
// ---------------------------------------------------------------------------

/**
 * Single platform user DTO — the view presented to platform admins.
 */
export const PlatformUserSchema = z.object({
  id: z.string().uuid(),
  email: z.string().email(),
  active: z.boolean(),
  firstName: z.string().optional().nullable(),
  lastName: z.string().optional().nullable(),
  sector: z.string().optional().nullable(),
  phone: z.string().optional().nullable(),
  roles: z.array(z.string()),
});

export type PlatformUser = z.infer<typeof PlatformUserSchema>;

/**
 * GET /platform/companies/:id/users response wrapper.
 */
export const PlatformUserListResponseSchema = z.object({
  users: z.array(PlatformUserSchema),
});

export type PlatformUserListResponse = z.infer<typeof PlatformUserListResponseSchema>;

// ---------------------------------------------------------------------------
// AZ-11: Per-User Capability Grant Contracts
// ---------------------------------------------------------------------------

/**
 * PUT /platform/companies/:id/users/:userId/capabilities request body (AZ-10.2).
 * Replace semantics: the full new set; empty array clears all grants.
 */
export const SetUserCapabilitiesBodySchema = z.object({
  capabilities: z.array(z.string()),
});

export type SetUserCapabilitiesBody = z.infer<typeof SetUserCapabilitiesBodySchema>;

/**
 * GET /platform/companies/:id/users/:userId/capabilities response (AZ-10.1).
 * Scenario AZ-11-A: no companyId / userId fields.
 */
export const UserCapabilityResponseSchema = z.object({
  roleCaps: z.array(z.string()),
  userGrants: z.array(z.string()),
  effective: z.array(z.string()),
});

export type UserCapabilityResponse = z.infer<typeof UserCapabilityResponseSchema>;

/**
 * GET /platform/companies/:id/users/:userId/access (UM-7).
 * Full access view: user profile + role-derived caps + per-user grants + capability catalog.
 */
export const UserAccessResponseSchema = z.object({
  user: PlatformUserSchema,
  roleCapabilities: z.array(z.string()),
  grantedCapabilities: z.array(z.string()),
  /** Full capability catalog for the picker UI (keys the platform admin can grant). */
  catalog: z.array(z.object({ key: z.string(), label: z.string(), moduleKey: z.string() })),
});

export type UserAccessResponse = z.infer<typeof UserAccessResponseSchema>;
